import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import styles from './EditionsPage.module.css';

interface Edition {
  id: string;
  title: string;
  date: string;
  description?: string;
  cover_url?: string;
}

const EditionsPage: React.FC = () => {
  const [editions, setEditions] = useState<Edition[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEditions = async () => {
      try {
        const { data, error } = await supabase
          .from('b2b_editions')
          .select('id, title, date, description, cover_url')
          .order('date', { ascending: true });

        if (error) throw error;
        setEditions(data || []);
      } catch {
        setEditions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEditions();
  }, []);

  const today = new Date().toISOString().split('T')[0];
  const upcoming = editions.filter((e) => e.date >= today);
  const past = editions.filter((e) => e.date < today).reverse();

  const formatDay = (dateStr: string) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.getDate().toString();
  };

  const formatMonthYear = (dateStr: string) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <h1 className={styles.title}>The Editions</h1>
          <p className={styles.loadingText}>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <h1 className={styles.title}>The Editions</h1>

        {upcoming.length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Upcoming</h2>
            <div className={styles.upcomingGrid}>
              {upcoming.map((edition) => (
                <div key={edition.id} className={styles.upcomingCard}>
                  <span className={styles.upcomingDay}>{formatDay(edition.date)}</span>
                  <span className={styles.upcomingMonth}>{formatMonthYear(edition.date)}</span>
                  <span className={styles.locationBadge}>Paris</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {past.length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Past Editions</h2>
            <div className={styles.pastList}>
              {past.map((edition) => (
                <Link
                  key={edition.id}
                  to={`/members/editions/${edition.id}`}
                  className={styles.pastCard}
                >
                  <div className={styles.pastCover}>
                    {edition.cover_url ? (
                      <img src={edition.cover_url} alt={edition.title} />
                    ) : (
                      <div className={styles.pastPlaceholder} />
                    )}
                  </div>
                  <div className={styles.pastContent}>
                    <span className={styles.pastDate}>{formatDate(edition.date)}</span>
                    <h3 className={styles.pastTitle}>{edition.title}</h3>
                    {edition.description && (
                      <p className={styles.pastDesc}>{edition.description}</p>
                    )}
                    <span className={styles.pastLink}>View details →</span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {editions.length === 0 && (
          <p className={styles.loadingText}>No editions found.</p>
        )}
      </div>
    </div>
  );
};

export default EditionsPage;
